import { upperFirst } from 'lodash-es'

import {
  CareInsightsZendeskArticles,
  ProviderInsightsSubAreaGerundFormatLabels,
  ProviderInsightsSubAreaName,
} from './constants'
import { SkillsGlossaryDefinitions } from './skillsGlossaryDefinitions'

export interface SkillsGlossaryEntry {
  subAreaName: ProviderInsightsSubAreaName
  term: string
  definition: string
  learnMoreUrl?: string
}

// Skills that are not visible to providers, so they get no glossary entry and no underline.
const isHiddenSkill = (subAreaName: ProviderInsightsSubAreaName) =>
  subAreaName.startsWith('RISK_MANAGEMENT_') ||
  subAreaName === ProviderInsightsSubAreaName.ATTUNEMENT_AND_RAPPORT_NON_VERBALS

const getZendeskArticleUrl = (subAreaName: ProviderInsightsSubAreaName) => {
  for (const articles of Object.values(CareInsightsZendeskArticles)) {
    const match = articles.find((article) => article.relatedSubAreas.includes(subAreaName))
    if (match) {
      return match.url
    }
  }
  return undefined
}

export const isSkillInGlossary = (
  subAreaName: ProviderInsightsSubAreaName,
  definitions: Partial<Record<ProviderInsightsSubAreaName, string>> = SkillsGlossaryDefinitions,
) => !isHiddenSkill(subAreaName) && !!definitions[subAreaName]

/**
 * Glossary entries sorted alphabetically by displayed (gerund-format) term. The term starts with a capital letter in the
 * glossary; the underlined skill inside a summary sentence keeps its lowercase label.
 */
export const getSkillsGlossaryEntries = (
  definitions: Partial<Record<ProviderInsightsSubAreaName, string>> = SkillsGlossaryDefinitions,
): SkillsGlossaryEntry[] =>
  Object.values(ProviderInsightsSubAreaName)
    .flatMap((subAreaName) => {
      const definition = definitions[subAreaName]
      if (!definition || isHiddenSkill(subAreaName)) {
        return []
      }
      return [
        {
          subAreaName,
          term: upperFirst(ProviderInsightsSubAreaGerundFormatLabels[subAreaName]),
          definition,
          learnMoreUrl: getZendeskArticleUrl(subAreaName),
        },
      ]
    })
    .sort((a, b) => a.term.localeCompare(b.term))
